import React from "react"
import { BrowserRouter as Router, Route, Routes, useLocation, Link } from "react-router-dom"
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AuthProvider, useAuth } from "./contexts/AuthContext"
import { useIsAdmin, useAssignmentSetMetadata } from "./hooks/queries"
import { ProgramProvider, useProgram } from "./contexts/ProgramContext"
import LoginPage from "./pages/LoginPage"
import AuthVerifyPage from "./pages/AuthVerifyPage"
import RootDispatch from "./pages/RootDispatch"
import HomePage from "./pages/HomePage"
import AssignmentsPage from "./pages/AssignmentsPage"
import SeatingChartPage from "./pages/SeatingChartPage"
import { RosterPage } from "./pages/RosterPage"
import { AdminDashboard } from "./pages/admin/AdminDashboard"
import AdminHelpPage from "./pages/admin/AdminHelpPage"
import InviteAcceptPage from "./pages/InviteAcceptPage"
import ProgramSelectorPage from "./pages/ProgramSelectorPage"
import RosterPrintPage from "./pages/RosterPrintPage"
import HelpPage from "./pages/HelpPage"
import LegalPage from "./pages/LegalPage"
import NotFoundPage from "./pages/NotFoundPage"
import ProtectedRoute from "./components/ProtectedRoute"

function useShowChrome() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return false;
  const hiddenPaths = ['/login', '/auth/verify', '/invite/'];
  return !hiddenPaths.some(p => location.pathname.startsWith(p));
}

export function NavBar() {
  const { user, signOut } = useAuth();
  const { currentProgram, programs } = useProgram();
  const { data: isAdmin } = useIsAdmin(!!user);
  const { data: assignmentMeta } = useAssignmentSetMetadata(currentProgram?.id ?? null);
  const location = useLocation();
  if (!user) return null;
  const hasProgram = !!currentProgram;
  const isAdminPage = location.pathname.startsWith('/admin');
  const isHelpPage = location.pathname === '/help';
  return (
    <nav className="no-print sticky top-0 z-20 bg-white border-b px-4 h-12 flex gap-4 text-sm items-center">
      {(hasProgram || isAdminPage || isHelpPage) && <>
        <Link to="/home" className="text-muted-foreground hover:text-foreground transition-colors">Home</Link>
        <Link to="/roster" className="text-muted-foreground hover:text-foreground transition-colors">Roster</Link>
        {assignmentMeta && (
          <Link to="/table-assignments" className="text-muted-foreground hover:text-foreground transition-colors">Assignments</Link>
        )}
        <Link to="/help" className="text-muted-foreground hover:text-foreground transition-colors">Help</Link>
        {programs.length > 1 && (
          <Link to="/select-program" className="text-muted-foreground hover:text-foreground transition-colors">Programs</Link>
        )}
      </>}
      <div className="ml-auto flex gap-4 items-center">
        <div id="nav-actions-slot" className="flex items-center gap-3" />
        {isAdmin && <Link to="/admin" className="text-muted-foreground hover:text-foreground transition-colors">Admin</Link>}
        <span className="text-muted-foreground">{user.email}</span>
        <button onClick={() => signOut()} className="text-muted-foreground hover:text-foreground transition-colors">Logout</button>
      </div>
    </nav>
  );
}

function Footer() {
  const showChrome = useShowChrome();
  if (!showChrome) return null;
  return (
    <footer className="no-print border-t px-4 py-2 text-xs text-muted-foreground text-center">
      Developed in collaboration with{' '}
      <a
        href="https://www.buildingbridgestogether.net/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline"
      >
        Building Bridges Together™
      </a>
    </footer>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,  // 2 min — data under 2 min old served from cache
      gcTime: 5 * 60 * 1000,     // 5 min — unused cache entries garbage collected
      retry: (failureCount, error) => {
        // Don't retry auth or permission errors — they're not transient
        if (error?.name === 'AuthenticationError') return false;
        if (error instanceof Error && /\b(40[13])\b/.test(error.message)) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
})

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ProgramProvider>
        <QueryClientProvider client={queryClient}>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <div className="flex flex-col min-h-screen">
            <NavBar />
            <div className="flex-1">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/verify" element={<AuthVerifyPage />} />
              <Route path="/invite/:token" element={<InviteAcceptPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/legal" element={<LegalPage />} />

              <Route
                path="/select-program"
                element={
                  <ProtectedRoute>
                    <ProgramSelectorPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <RootDispatch />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roster"
                element={
                  <ProtectedRoute>
                    <RosterPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/help"
                element={
                  <ProtectedRoute>
                    <AdminHelpPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/table-assignments"
                element={
                  <ProtectedRoute>
                    <AssignmentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/table-assignments/seating"
                element={
                  <ProtectedRoute>
                    <SeatingChartPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/table-assignments/roster-print"
                element={
                  <ProtectedRoute>
                    <RosterPrintPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </div>
            <Footer />
            </div>
          </Router>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ProgramProvider>
    </AuthProvider>
  )
}

export default App
